"use client";
import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../../context/AppContext";
import { useVisual } from "../../context/VisualContext";
import { useUser } from "../../context/UserContext";
import ParticleSystem from "../ui/ParticleSystem";
import ChatInterface from "../ui/ChatInterface";
import { useHandleStreamResponse, useVisualEffects } from "../../hooks/useEffects";

const CosmicMainComponent = () => {
  const { activeSection, setActiveSection, isLoading, setLoading, addNotification } = useApp();
  const { settings: visualSettings } = useVisual();
  const { isAuthenticated, userId } = useUser();
  const { streamResponse, isStreaming, streamedContent, resetStream } = useHandleStreamResponse();
  const [reflectionPrompt, setReflectionPrompt] = useState("");
  const [reflection, setReflection] = useState("");
  const [legacyEvents, setLegacyEvents] = useState([]);
  const [acceptanceMessages, setAcceptanceMessages] = useState([]);
  
  // Simulated data for demo purposes
  useEffect(() => {
    // Legacy timeline events
    setLegacyEvents([
      { id: 1, date: "2020-01-15", title: "First Memory", content: "This is a simulated memory for the timeline." },
      { id: 2, date: "2021-03-22", title: "Important Milestone", content: "This is a simulated milestone for the timeline." },
      { id: 3, date: "2022-07-10", title: "Personal Achievement", content: "This is a simulated achievement for the timeline." },
      { id: 4, date: "2023-11-05", title: "Reflection Point", content: "This is a simulated reflection point for the timeline." },
    ]);
    
    // Acceptance wall messages
    setAcceptanceMessages([
      { id: 1, content: "I accept my past and embrace my future.", timestamp: "2023-05-10T14:30:00Z" },
      { id: 2, content: "Every experience shapes who I am today.", timestamp: "2023-06-22T09:15:00Z" },
      { id: 3, content: "I am grateful for the lessons I've learned.", timestamp: "2023-08-17T16:45:00Z" },
    ]);
  }, []);

  // Generate reflection
  const generateReflection = async () => {
    if (!reflectionPrompt.trim()) {
      addNotification("Please enter a reflection prompt", "warning");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await streamResponse(
        '/api/reflection-generator',
        { prompt: reflectionPrompt }
      );
      
      setReflection(response);
      resetStream();
      addNotification("Reflection generated successfully", "success");
    } catch (error) {
      console.error("Error generating reflection:", error);
      addNotification("Failed to generate reflection", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add acceptance message
  const addAcceptanceMessage = (content) => {
    const newMessage = {
      id: Date.now(),
      content,
      timestamp: new Date().toISOString()
    };
    
    setAcceptanceMessages(prev => [newMessage, ...prev]);
    addNotification("Message added to acceptance wall", "success");
  };

  // Add legacy event
  const addLegacyEvent = (event) => {
    const newEvent = {
      id: Date.now(),
      ...event
    };
    
    setLegacyEvents(prev => [...prev, newEvent]);
    addNotification("Event added to legacy timeline", "success");
  };

  // Render legacy timeline
  const renderLegacyTimeline = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold cosmic-accent">Legacy Timeline</h2>
        
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-nebula-primary"></div>
          
          {/* Timeline events */}
          <div className="space-y-8 ml-12">
            {legacyEvents.map(event => (
              <div key={event.id} className="relative">
                {/* Timeline dot */}
                <div className="absolute -left-10 top-3 w-4 h-4 rounded-full bg-nebula-primary"></div>
                
                {/* Event content */}
                <div className="cosmic-card p-4">
                  <div className="text-sm text-gray-400">{new Date(event.date).toLocaleDateString()}</div>
                  <h3 className="text-lg font-semibold">{event.title}</h3>
                  <p className="mt-2">{event.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Add event form */}
        <div className="cosmic-card p-4 mt-6">
          <h3 className="text-lg font-semibold mb-3">Add New Memory</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            addLegacyEvent({
              date: formData.get('date'),
              title: formData.get('title'),
              content: formData.get('content')
            });
            e.target.reset();
          }}>
            <div className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Date</label>
                <input type="date" name="date" required className="cosmic-input w-full" />
              </div>
              <div>
                <label className="block text-sm mb-1">Title</label>
                <input type="text" name="title" required className="cosmic-input w-full" />
              </div>
              <div>
                <label className="block text-sm mb-1">Content</label>
                <textarea name="content" required className="cosmic-input w-full h-24"></textarea>
              </div>
              <button type="submit" className="cosmic-button bg-nebula-gradient">Add to Timeline</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Render acceptance wall
  const renderAcceptanceWall = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold cosmic-accent">Acceptance Wall</h2>
        
        {/* Add message form */}
        <div className="cosmic-card p-4">
          <h3 className="text-lg font-semibold mb-3">Add New Message</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            addAcceptanceMessage(formData.get('message'));
            e.target.reset();
          }}>
            <div className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Your Message</label>
                <textarea name="message" required className="cosmic-input w-full h-24" placeholder="Write an acceptance message..."></textarea>
              </div>
              <button type="submit" className="cosmic-button bg-nebula-gradient">Add to Wall</button>
            </div>
          </form>
        </div>
        
        {/* Messages */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {acceptanceMessages.map(message => (
            <div key={message.id} className="cosmic-card p-4">
              <p className="italic">{message.content}</p>
              <div className="text-sm text-gray-400 mt-2">
                {new Date(message.timestamp).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render reflection section
  const renderReflection = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold cosmic-accent">AI-Guided Reflection</h2>
        
        {/* Prompt input */}
        <div className="cosmic-card p-4">
          <h3 className="text-lg font-semibold mb-3">Reflection Prompt</h3>
          <div className="space-y-3">
            <textarea 
              value={reflectionPrompt}
              onChange={(e) => setReflectionPrompt(e.target.value)}
              className="cosmic-input w-full h-24"
              placeholder="Enter a topic or question for reflection..."
            ></textarea>
            <button 
              onClick={generateReflection}
              disabled={isLoading || isStreaming || !reflectionPrompt.trim()}
              className="cosmic-button bg-nebula-gradient"
            >
              Generate Reflection
            </button>
          </div>
        </div>
        
        {/* Reflection output */}
        {(reflection || isStreaming) && (
          <div className="cosmic-card p-4">
            <h3 className="text-lg font-semibold mb-3">Your Reflection</h3>
            <div className="prose prose-invert max-w-none">
              {isStreaming ? streamedContent : reflection}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cosmic-dark to-cosmic-medium">
      <div className="max-w-6xl mx-auto p-4 relative">
        {/* Particle background */}
        <div className="fixed inset-0 z-0">
          <ParticleSystem 
            density={visualSettings.particleDensity} 
            colorScheme={visualSettings.colorScheme}
            interactive={true}
          />
        </div>
        
        {/* Header */}
        <header className="relative z-10 py-6 mb-8">
          <h1 className="text-4xl font-bold text-center cosmic-accent">Infinite AI Project</h1>
          <p className="text-center text-gray-300 mt-2">
            A cosmic-themed AI assistant for personal legacy and reflection
          </p>
        </header>
        
        {/* Navigation */}
        <nav className="relative z-10 mb-8">
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setActiveSection('legacy')}
              className={`cosmic-button px-6 py-2 ${
                activeSection === 'legacy' 
                  ? 'bg-nebula-gradient' 
                  : 'bg-cosmic-medium hover:bg-cosmic-light'
              }`}
            >
              Legacy Timeline
            </button>
            <button
              onClick={() => setActiveSection('acceptance')}
              className={`cosmic-button px-6 py-2 ${
                activeSection === 'acceptance' 
                  ? 'bg-nebula-gradient' 
                  : 'bg-cosmic-medium hover:bg-cosmic-light'
              }`}
            >
              Acceptance Wall
            </button>
            <button
              onClick={() => setActiveSection('reflection')}
              className={`cosmic-button px-6 py-2 ${
                activeSection === 'reflection' 
                  ? 'bg-nebula-gradient' 
                  : 'bg-cosmic-medium hover:bg-cosmic-light'
              }`}
            >
              Reflection
            </button>
            <button
              onClick={() => setActiveSection('chat')}
              className={`cosmic-button px-6 py-2 ${
                activeSection === 'chat' 
                  ? 'bg-nebula-gradient' 
                  : 'bg-cosmic-medium hover:bg-cosmic-light'
              }`}
            >
              AI Chat
            </button>
          </div>
        </nav>
        
        {/* Main content */}
        <main className="relative z-10 mb-12">
          <div className="cosmic-card p-6">
            {activeSection === 'legacy' && renderLegacyTimeline()}
            {activeSection === 'acceptance' && renderAcceptanceWall()}
            {activeSection === 'reflection' && renderReflection()}
            {activeSection === 'chat' && <ChatInterface />}
          </div>
        </main>
        
        {/* Footer */}
        <footer className="relative z-10 text-center text-gray-400 py-4">
          <p>Created by Manus and <a href="https://HichamMneimne.com" className="text-nebula-primary hover:text-nebula-accent transition-colors">HichamMneimne.com</a> &copy; {new Date().getFullYear()}</p>
        </footer>
      </div>
    </div>
  );
};

export default CosmicMainComponent;
